import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosConfig';
import './AnnouncementModal.css';

/**
 * The full-screen system announcement. Deliberately NOT dismissible until its
 * timer runs out — no close button, no backdrop click, Escape swallowed — so a
 * maintenance / outage notice cannot be clicked away and missed. When the
 * countdown (driven off the server's `expiresAt`, not a local duration, so
 * every tab agrees) reaches zero it closes itself.
 *
 * If the announcement allows replies, a small box lets the recipient send one
 * line straight back to the admins (POST /:id/responses -> live to their dock).
 * Sending does NOT close the popup and is not required — the timer still owns
 * when it goes away. The admin who posted it never sees this box; they read
 * replies in the floating dock instead.
 */
const AnnouncementModal = ({ announcement, onDismiss, isAdmin = false }) => {
    const expiresAt = announcement ? new Date(announcement.expiresAt).getTime() : 0;
    const [remaining, setRemaining] = useState(0);
    const [reply, setReply] = useState('');
    const [replyState, setReplyState] = useState('idle'); // idle | sending | sent | error

    useEffect(() => {
        if (!announcement) return undefined;

        const tick = () => {
            const secs = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            setRemaining(secs);
            if (secs <= 0) onDismiss(announcement.id);
        };
        tick();
        const timer = setInterval(tick, 500);
        return () => clearInterval(timer);
    }, [announcement, expiresAt, onDismiss]);

    // Reset the reply box whenever a different announcement comes up.
    useEffect(() => {
        setReply('');
        setReplyState('idle');
    }, [announcement?.id]);

    // Swallow Escape while the popup is up.
    useEffect(() => {
        if (!announcement) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [announcement]);

    if (!announcement) return null;

    const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');
    const canReply = announcement.allowResponses && !isAdmin;

    const sendReply = async () => {
        const text = reply.trim();
        if (!text || replyState === 'sending') return;
        setReplyState('sending');
        try {
            await axiosInstance.post(`/api/announcements/${announcement.id}/responses`, { text });
            setReplyState('sent');
            setReply('');
        } catch (err) {
            console.error('[announcement] reply failed:', err);
            setReplyState('error');
        }
    };

    return (
        <div className="announcement-overlay" role="alertdialog" aria-modal="true" aria-label="System announcement">
            <div className="announcement-box">
                <div className="announcement-box__icon">📢</div>
                <div className="announcement-box__label">Announcement</div>
                <div className="announcement-box__message">{announcement.message}</div>
                <div className="announcement-box__timer">
                    Closes automatically in <strong>{mm}:{ss}</strong>
                </div>
                {announcement.createdByName && (
                    <div className="announcement-box__by">— {announcement.createdByName}</div>
                )}

                {canReply && (
                    <div className="announcement-reply">
                        {replyState === 'sent' ? (
                            <div className="announcement-reply__done">
                                ✓ Sent to the admin.
                                <button type="button" onClick={() => setReplyState('idle')}>
                                    Send another
                                </button>
                            </div>
                        ) : (
                            <>
                                <label className="announcement-reply__label">Need to reply?</label>
                                <textarea
                                    className="announcement-reply__input"
                                    rows={2}
                                    maxLength={2000}
                                    placeholder="Type a message for the admin…"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    disabled={replyState === 'sending'}
                                />
                                <div className="announcement-reply__row">
                                    {replyState === 'error' && (
                                        <span className="announcement-reply__err">Could not send — try again.</span>
                                    )}
                                    <button
                                        type="button"
                                        className="announcement-reply__send"
                                        onClick={sendReply}
                                        disabled={!reply.trim() || replyState === 'sending'}
                                    >
                                        {replyState === 'sending' ? 'Sending…' : 'Send reply'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnnouncementModal;
